/* eslint-disable jsx-a11y/anchor-is-valid */
// @flow
import React, { useState, useEffect } from 'react';

import Table from '../../components/Table';
import { useTranslation } from 'react-i18next';
import { Loading } from 'react-loading-ui';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useSelector } from 'react-redux';
import { getTelemetryHistory, getUserRequestAPI } from '../../API/RDPMS_API';
import moment from 'moment';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'material-design-icons/iconfont/material-icons.css';
import '../../../src/assets/scss/custom/components/_alert.scss';
import '../dashboard/Dashboard/Dashboard.css';
import HyperDatepicker from '../../components/Datepicker';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import {
    mapRequestDataToOptions,
    sizePerPageList,
    TelemetryHistoryColumns,
    buildGateMergedHistoryRowData,
} from './requestDataMapper';
import GraphChartComponent from '../../components/Charts/GraphChartComponent';
import CircuitPage from '../../components/Charts/CircuitPage';

import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import TelemetryGraphComponent from '../../components/Charts/TelemetryGraphComponent';

// DC ka data IPS ke naam se dikhana hai (sir ka decision)
const DC_TO_IPS_LABEL = {
    itc_battery_charging_current: 'ips_battery_charging_current',
    vtc_track_feed_charger_input_voltage: 'ips_input_voltage',
    vtc_track_feed_end_voltage_to_rails: 'ips_110_dc_output_voltage',
    itc_track_feed_end_current: 'vtc_variable_track_resistance_voltage',
};

const mapParamLabel = (name) => DC_TO_IPS_LABEL[name] || name;
const mapAssetTypeLabel = (t) => (t === 'DCT' ? 'IPS' : t);

const formatParamName = (name) => {
    if (!name) return '';
    return name.split('_').join(' ').toUpperCase();
};

const parseGraphTime = (t) => {
    const tMoment = moment(t, 'YYYYMMDDTHHmmss,SSS');
    return tMoment.isValid() ? tMoment.valueOf() : null;
};

const MAX_GRAPH_POINTS = 500;

const downsampleSeries = (categories, dataPoints, maxPoints) => {
    if (categories.length <= maxPoints) {
        return { categories, dataPoints };
    }

    const bucketSize = Math.ceil(categories.length / maxPoints);
    const newCategories = [];
    const newData = [];

    for (let i = 0; i < categories.length; i += bucketSize) {
        const catBucket = categories.slice(i, i + bucketSize);
        const valBucket = dataPoints.slice(i, i + bucketSize);

        const hasGap = valBucket.some((v) => v === null || v === undefined);

        newCategories.push(catBucket[Math.floor(catBucket.length / 2)]);

        if (hasGap) {
            newData.push(null);
        } else {
            newData.push(valBucket.reduce((a, b) => a + b, 0) / valBucket.length);
        }
    }

    return { categories: newCategories, dataPoints: newData };
};

const buildGraphChartData = (rawData, assetType, assetNumber) => {
    if (!assetType || !assetNumber) return { categories: [], series: [] };

    const timeSet = new Set();
    const paramSet = new Set();
    const lookup = new Map();

    for (let i = 0; i < rawData.length; i++) {
        const e = rawData[i];
        if (e.assetType === assetType.value && e.assetNumber === assetNumber.value) {
            timeSet.add(e.time);
            paramSet.add(e.paramName);
            lookup.set(`${e.paramName}__${e.time}`, e.value);
        }
    }

    let categories = [...timeSet].sort((a, b) => a - b);
    const paramNames = [...paramSet];

    let series = paramNames.map((param) => ({
        // name: formatParamName(param),
        name: formatParamName(mapParamLabel(param)),
        data: categories.map((cat) => {
            const val = lookup.get(`${param}__${cat}`);
            return val !== undefined ? val : null;
        }),
    }));

    if (categories.length > MAX_GRAPH_POINTS) {
        let newCategories = categories;
        const downsampledSeries = [];

        series.forEach((s) => {
            const result = downsampleSeries(categories, s.data, MAX_GRAPH_POINTS);
            newCategories = result.categories;
            downsampledSeries.push({ name: s.name, data: result.dataPoints });
        });

        categories = newCategories;
        series = downsampledSeries;
    }

    return { categories, series };
};

// ---- NEW: ELC/MLC gate params ko merged columns me dikhane ke liye ----
const GATE_MERGE_ASSET_TYPES = ['ELC', 'MLC'];

const GATE_PAIR_CONFIGS = [
    {
        keys: ['gate_open', 'gate_closed'],
        label: 'Gate Open/Closed',
        display: {
            gate_open: { true: 'Open', false: 'Closed' },
            gate_closed: { true: 'Closed', false: 'Open' },
        },
    },
    {
        keys: ['gate_locked', 'gate_unlocked'],
        label: 'Gate Locked/Unlocked',
        display: {
            gate_locked: { true: 'Locked', false: 'Unlocked' },
            gate_unlocked: { true: 'Unlocked', false: 'Locked' },
        },
    },
    {
        keys: ['gate_opening', 'gate_closing'],
        label: 'Gate Opening/Closing',
        display: {
            gate_opening: { true: 'Opening', false: 'Closing' },
            gate_closing: { true: 'Closing', false: 'Opening' },
        },
    },
];

const buildGateMergedRowData = (rowData) => {
    if (!rowData || !GATE_MERGE_ASSET_TYPES.includes(rowData.assetType)) {
        return rowData;
    }

    const originalParamKeys = rowData.paramKeys || [];
    const consumedKeys = new Set();
    const displayParamKeys = [];
    const columnPlan = [];

    originalParamKeys.forEach((key) => {
        if (consumedKeys.has(key)) return;

        const pairConfig = GATE_PAIR_CONFIGS.find((cfg) => cfg.keys.includes(key));

        if (pairConfig) {
            pairConfig.keys.forEach((k) => consumedKeys.add(k));
            displayParamKeys.push(pairConfig.label);
            columnPlan.push({ type: 'merged', config: pairConfig });
        } else {
            consumedKeys.add(key);
            displayParamKeys.push(key);
            columnPlan.push({ type: 'plain', key });
        }
    });

    const displayRows = (rowData.rows || []).map((row) => {
        const values = columnPlan.map((plan) => {
            if (plan.type === 'plain') {
                const idx = originalParamKeys.indexOf(plan.key);
                return row.values[idx];
            }

            for (const key of plan.config.keys) {
                const idx = originalParamKeys.indexOf(key);
                const rawVal = row.values[idx];
                if (rawVal !== undefined && rawVal !== '-') {
                    return plan.config.display[key][rawVal] || rawVal;
                }
            }
            return '-';
        });

        return { dateTime: row.dateTime, values };
    });

    return {
        ...rowData,
        paramKeys: displayParamKeys,
        rows: displayRows,
    };
};
// ---- END NEW ----

const TelemetryHistory = () => {
    const { auth } = useSelector((state) => ({
        auth: state.Auth,
    }));
    const userRole = auth?.user?.role;

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [data, setData] = useState([]);
    const [limit, setLimit] = useState(5);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);

    const [selectedViews, setSelectedViews] = useState({ label: 'Table', value: 'table' });

    const [circuit, setCircuit] = useState([]);

    // ---- GRAPH RELATED STATE (Current + Voltage) ----
    const [rawCurrentGraphData, setRawCurrentGraphData] = useState([]);
    const [rawVoltageGraphData, setRawVoltageGraphData] = useState([]);
    const [currentGraphData, setCurrentGraphData] = useState({ categories: [], series: [] });
    const [voltageGraphData, setVoltageGraphData] = useState({ categories: [], series: [] });

    const [graphAssetTypeOptions, setGraphAssetTypeOptions] = useState([]);
    const [selectedGraphAssetType, setSelectedGraphAssetType] = useState(null);
    const [graphAssetNumberOptions, setGraphAssetNumberOptions] = useState([]);
    const [selectedGraphAssetNumber, setSelectedGraphAssetNumber] = useState(null);
    // ---- END GRAPH RELATED STATE ----

    // ---- CIRCUIT RELATED STATE ----
    const [circuitAssetTypeOptions, setCircuitAssetTypeOptions] = useState([]);
    const [selectedCircuitAssetType, setSelectedCircuitAssetType] = useState(null);
    const [circuitAssetNumberOptions, setCircuitAssetNumberOptions] = useState([]);
    const [selectedCircuitAssetNumber, setSelectedCircuitAssetNumber] = useState(null);
    // ---- END CIRCUIT RELATED STATE ----

    const [selectedStartDate, setSelectedStartDate] = useState(() => {
        const now = new Date();
        const last24Hours = new Date();
        last24Hours.setHours(now.getHours() - 24);
        return last24Hours;
    });
    const [selectedEndDate, setSelectedEndDate] = useState(new Date());

    const [selectedZones, setSelectedZones] = useState([
        {
            label: 'ALL',
            value: 'all',
        },
    ]);
    const [selectedDivisions, setSelectedDivisions] = useState([
        {
            label: 'ALL',
            value: 'all',
        },
    ]);
    const [selectedStation, setSelectedStation] = useState([
        {
            label: 'ALL',
            value: 'all',
        },
    ]);
    const [selectedAssetsType, setSelectedAssetsType] = useState([
        {
            label: 'ALL',
            value: 'all',
        },
    ]);
    const [selectedAssetsNumber, setSelectedAssetsNumber] = useState([
        {
            label: 'ALL',
            value: 'all',
        },
    ]);

    const [stations, setStations] = useState([{ value: 'table', label: 'Table' }]);
    const [zones, setZones] = useState([]);
    const [divisions, setDivisions] = useState([]);
    const [assetsTypeCode, setAssetsTypeCode] = useState([]);
    const [assetsType, setAssetsType] = useState([]);
    const [assetsNumber, setAssetsNumber] = useState([]);

    const [statusMessage, setStatusMessage] = useState('');

    // ---- STATE FOR PARAMETER DETAIL MODAL ----
    const [selectedRowData, setSelectedRowData] = useState(null);
    const [rowDetailsList, setRowDetailsList] = useState([]);
    // ---- END STATE ----

    useEffect(() => {
        const fetchStation = async () => {
            await getRequestData(auth.user.id);
        };

        fetchStation();
    }, []);

    useEffect(() => {
        if (stations && stations.length > 0) {
            getTelemetryHistoryData(limit, pageNumber);
        }
    }, [stations]);

    useEffect(() => {
        if (!selectedGraphAssetType) {
            setGraphAssetNumberOptions([]);
            setSelectedGraphAssetNumber(null);
            return;
        }

        const numbers = [
            ...new Set(
                [...rawCurrentGraphData, ...rawVoltageGraphData]
                    .filter((e) => e.assetType === selectedGraphAssetType.value)
                    .map((e) => e.assetNumber)
            ),
        ].map((n) => ({ label: n, value: n }));

        setGraphAssetNumberOptions(numbers);
        setSelectedGraphAssetNumber(numbers[0] || null);
    }, [selectedGraphAssetType, rawCurrentGraphData, rawVoltageGraphData]);

    useEffect(() => {
        setCurrentGraphData(buildGraphChartData(rawCurrentGraphData, selectedGraphAssetType, selectedGraphAssetNumber));
    }, [selectedGraphAssetNumber, selectedGraphAssetType, rawCurrentGraphData]);

    useEffect(() => {
        setVoltageGraphData(buildGraphChartData(rawVoltageGraphData, selectedGraphAssetType, selectedGraphAssetNumber));
    }, [selectedGraphAssetNumber, selectedGraphAssetType, rawVoltageGraphData]);

    useEffect(() => {
        if (!selectedCircuitAssetType) {
            setCircuitAssetNumberOptions([]);
            setSelectedCircuitAssetNumber(null);
            return;
        }

        const numbers = [
            ...new Set(circuit.filter((c) => c.assetType === selectedCircuitAssetType.value).map((c) => c.assetNumber)),
        ].map((n) => ({ label: n, value: n }));

        setCircuitAssetNumberOptions(numbers);
        setSelectedCircuitAssetNumber(numbers[0] || null);
    }, [selectedCircuitAssetType, circuit]);

    const { t } = useTranslation();

    const formatDateTime = (date) => {
        if (!date) return {};

        return {
            date: `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(
                2,
                '0'
            )}/${date.getFullYear()}`,
            time: `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(
                date.getSeconds()
            ).padStart(2, '0')}`,
        };
    };

    const handleShowParamModal = (rowInfo) => {
        setSelectedRowData(rowInfo);
    };

    const handleTableRowClick = (e) => {
        const trElem = e.target.closest('tr');
        if (!trElem) return;

        const tbodyElem = trElem.closest('tbody');
        if (!tbodyElem) return;

        const rowIndex = Array.from(tbodyElem.children).indexOf(trElem);
        if (rowIndex === -1) return;

        const rowInfo = rowDetailsList[rowIndex];
        if (rowInfo) {
            setSelectedRowData(rowInfo);
        }
    };

    const getTelemetryHistoryData = async (limits, pages) => {
        Loading();
        let limitNumber = limits ? limits : limit;
        let pagesNumber = pages ? pages : pageNumber;

        const { date: start_date, time: start_time } = formatDateTime(selectedStartDate);
        const { date: end_date, time: end_time } = formatDateTime(selectedEndDate);

        const zone = selectedZones.some((z) => z.value === 'all') ? [] : selectedZones.map((z) => z.value);

        const division = selectedDivisions.some((d) => d.value === 'all') ? [] : selectedDivisions.map((d) => d.value);

        let station = [];
        if (userRole === 'super admin') {
            station = selectedStation.some((s) => s.value === 'all') ? [] : selectedStation.map((s) => s.value);
        } else {
            if (selectedStation.length > 0 && !selectedStation.some((s) => s.value === 'all')) {
                station = selectedStation.map((s) => s.value);
            } else {
                station = stations.filter((s) => s.value !== 'all').map((s) => s.value);
            }
        }

        const assetsType = selectedAssetsType.some((a) => a.value === 'all')
            ? []
            : selectedAssetsType.map((a) => a.value);

        const assetsNumber = selectedAssetsNumber.some((item) => item.value === 'all')
            ? []
            : selectedAssetsNumber.map((item) => ({
                  sc: item.sc,
                  asset_number_code: item.label,
              }));

        let request = {
            start_date: start_date,
            start_time: start_time,

            end_date: end_date,
            end_time: end_time,

            request: {
                zone: zone,
                division: division,
                station: station,
                asset_type: assetsType,
                asset_number: assetsNumber,
            },
            page_number: pagesNumber,
            page_size: limitNumber,
        };

        let response = await getTelemetryHistory(request);
        // console.log('Telemetry History Response:', response);

        if (response) {
            let srNo = (pagesNumber - 1) * limitNumber + 1;

            let dataArray = [];
            let rowDetailsArray = [];
            let circuitArray = [];

            const formatTimestamp = (ts) => {
                const m = moment(ts, 'YYYYMMDDTHHmmss,SSS');
                return m.isValid() ? m.format('DD/MM/YYYY HH:mm:ss') : ts;
            };

            response?.zone?.forEach((z) => {
                z.division?.forEach((d) => {
                    d.station?.forEach((s) => {
                        s.asset_type?.forEach((at) => {
                            at.asset_number?.forEach((asn) => {
                                const paramKeys = Object.keys(asn).filter(
                                    (key) => key !== 'asnc' && key !== 'parameterCode'
                                );

                                const timeMap = {};
                                paramKeys.forEach((key) => {
                                    const prv = asn[key]?.prv || [];
                                    const prt = asn[key]?.prt || [];
                                    prv.forEach((value, idx) => {
                                        const ts = prt[idx];
                                        if (!ts) return;
                                        if (!timeMap[ts]) timeMap[ts] = {};
                                        timeMap[ts][key] = value;
                                    });
                                });

                                const timestamps = Object.keys(timeMap).sort().reverse();

                                const rowInfoForModal = {
                                    zoneCode: z.zc || '',
                                    divisionCode: d.dc || '',
                                    stationCode: s.sc || '',
                                    // assetType: at.astc || '',
                                    // assetNumber: asn.asnc || '',
                                    // paramKeys: paramKeys,

                                    assetType: mapAssetTypeLabel(at.astc) || '',
                                    assetNumber: asn.asnc || '',
                                    paramKeys: paramKeys.map(mapParamLabel),
                                    rows: timestamps.map((ts) => ({
                                        dateTime: formatTimestamp(ts),
                                        values: paramKeys.map((key) =>
                                            timeMap[ts][key] !== undefined ? String(timeMap[ts][key]) : '-'
                                        ),
                                    })),
                                };

                                rowDetailsArray.push(rowInfoForModal);

                                dataArray.push({
                                    srNo: srNo++,
                                    vcc: response?.vcc || '',
                                    vcn: response?.vcn || '',
                                    zoneCode: z.zc || '',
                                    divisionCode: d.dc || '',
                                    stationCode: s.sc || '',
                                    // assetType: at.astc || '',
                                    assetType: mapAssetTypeLabel(at.astc) || '',
                                    assetNumber: asn.asnc || '',

                                    action: (
                                        <button
                                            type="button"
                                            className="btn btn-sm p-0"
                                            data-bs-toggle="modal"
                                            data-bs-target="#parameterDetailModal"
                                            onClick={() => handleShowParamModal(rowInfoForModal)}
                                            title="View Parameters"
                                            style={{ border: 'none', background: 'transparent' }}>
                                            <i className="mdi mdi-eye"></i>
                                        </button>
                                    ),
                                });
                            });
                        });
                    });
                });
            });

            setPageNumber(response?.pagination?.currentPage);
            setPageCount(response?.pagination?.totalPages);
            setTotalCount(response?.pagination?.totalCount);
            setData(dataArray);
            setRowDetailsList(rowDetailsArray);

            setCircuit(response?.circuitRows || []);

            // const circuitTypeOptions = [...new Set((response?.circuitRows || []).map((c) => c.assetType))].map((t) => ({
            //     label: t,
            //     value: t,
            // }));

            const circuitTypeOptions = [...new Set((response?.circuitRows || []).map((c) => c.assetType))].map((t) => ({
                label: mapAssetTypeLabel(t),
                value: t,
            }));

            setCircuitAssetTypeOptions(circuitTypeOptions);
            setSelectedCircuitAssetType(circuitTypeOptions[0] || null);

            const currentGraphEntries = (response?.currentGraphRows || [])
                .map((e) => ({ ...e, time: parseGraphTime(e.time) }))
                .filter((e) => e.time !== null);

            const voltageGraphEntries = (response?.voltageGraphRows || [])
                .map((e) => ({ ...e, time: parseGraphTime(e.time) }))
                .filter((e) => e.time !== null);

            setRawCurrentGraphData(currentGraphEntries);
            setRawVoltageGraphData(voltageGraphEntries);

            const graphTypeOptions = [
                ...new Set([...currentGraphEntries, ...voltageGraphEntries].map((e) => e.assetType)),
            ].map((t) => ({
                label: t,
                value: t,
            }));

            setGraphAssetTypeOptions(graphTypeOptions);
            setSelectedGraphAssetType(graphTypeOptions[0] || null);

            setStatusMessage('Alerts telemetry history fetched successfully');
        }
        Loading();
    };

    const getRequestData = async (id) => {
        try {
            Loading(true);

            const response = await getUserRequestAPI(id);

            if (response) {
                const { stations, zones, divisions, assetsType, assetsTypeCode, assetsNumber } =
                    mapRequestDataToOptions(response);

                setStations(stations);
                setZones(zones);
                setDivisions(divisions);
                setAssetsType(assetsType);
                setAssetsTypeCode(assetsTypeCode);
                setAssetsNumber(assetsNumber);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            Loading(false);
        }
    };

    const handlePageChange = async (value) => {
        setPageNumber(1);
        setLimit(value);
        await getTelemetryHistoryData(value, 1);
    };

    const handlePageClick = async (value) => {
        setPageNumber(value);
        await getTelemetryHistoryData(limit, value);
    };

    const viewOptions = [
        { label: 'Table', value: 'table' },
        { label: 'Graph', value: 'graph' },
        { label: 'Circuit', value: 'circuit' },
    ];

    const downloadTelemetryHistoryExcel = () => {
        const exportData = data.map((row) => ({
            'Sr Number': row.srNo,
            VCC: row.vcc,
            VCN: row.vcn,
            'Zone Code': row.zoneCode,
            'Division Code': row.divisionCode,
            'Station Code': row.stationCode,
            'Asset Type': row.assetType,
            'Asset Number': row.assetNumber,
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(workbook, worksheet, 'Telemetry History');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
        });

        saveAs(blob, 'Telemetry_History.xlsx');
    };

    return (
        <>
            <ToastContainer />

            <div className=" py-3 " style={{ width: '100%' }}>
                <div className="container-fluid px-0">
                    <div className="row align-items-center gx-2 gy-2">
                        <div className="col-12 col-md-auto">
                            <h5
                                className="fw-bold mb-1"
                                style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    color: '#000',
                                    paddingLeft: '10px',
                                }}>
                                Telemetry History
                            </h5>
                        </div>
                    </div>
                </div>

                <div
                    className="report-alert-card my-2 p-1"
                    style={{
                        width: '100%',
                        boxShadow: '0px 0px 35px 0px rgba(154, 161, 171, 0.15)',
                        position: 'relative',
                        height: 'auto',
                    }}>
                    <div
                        className="container-fluid"
                        style={{
                            paddingLeft: '10px',
                            paddingRight: '10px',
                        }}>
                        <form>
                            <div className="row d-flex gx-3 gy-2">
                                <div className="col-md-3 form-group">
                                    <label htmlFor="startDate">Start Date</label>

                                    <div className="custom-datepicker-wrapper">
                                        <HyperDatepicker
                                            hideAddon={true}
                                            type="date"
                                            className="form-control"
                                            dateFormat="yyyy-MM-dd h:mm aa"
                                            value={selectedStartDate}
                                            maxDate={new Date()}
                                            showTimeSelect={true}
                                            timeFormat="h:mm aa"
                                            timeIntervals={15}
                                            onChange={(date) => setSelectedStartDate(date)}
                                        />
                                    </div>
                                </div>

                                <div className="col-md-3 form-group">
                                    <label htmlFor="endDate">End Date</label>

                                    <div className="custom-datepicker-wrapper">
                                        <HyperDatepicker
                                            hideAddon={true}
                                            type="date"
                                            className="form-control"
                                            defaultValue="2024-08-22"
                                            id="endDate"
                                            dateFormat="yyyy-MM-dd h:mm aa"
                                            value={selectedEndDate}
                                            maxDate={new Date()}
                                            showTimeSelect={true}
                                            timeFormat="h:mm aa"
                                            timeIntervals={15}
                                            onChange={(date) => {
                                                setSelectedEndDate(date);
                                            }}
                                        />
                                    </div>
                                </div>

                                <MultiSelectDropdown
                                    label="Zones"
                                    id="zoneSelect"
                                    options={zones}
                                    selectedValues={selectedZones}
                                    onChange={setSelectedZones}
                                />

                                <MultiSelectDropdown
                                    label="Divisions"
                                    id="divisionSelect"
                                    options={divisions}
                                    selectedValues={selectedDivisions}
                                    onChange={setSelectedDivisions}
                                />

                                <MultiSelectDropdown
                                    label="Station"
                                    id="stationSelect"
                                    options={stations}
                                    selectedValues={selectedStation}
                                    onChange={setSelectedStation}
                                />

                                <MultiSelectDropdown
                                    label="Assets Type"
                                    id="assetsSelect"
                                    options={assetsType}
                                    selectedValues={selectedAssetsType}
                                    onChange={setSelectedAssetsType}
                                />

                                <MultiSelectDropdown
                                    label="Assets Number"
                                    id="assetsNumberSelect"
                                    options={assetsNumber}
                                    selectedValues={selectedAssetsNumber}
                                    onChange={setSelectedAssetsNumber}
                                />

                                <MultiSelectDropdown
                                    label="View"
                                    id="viewSelect"
                                    options={viewOptions}
                                    selectedValues={selectedViews}
                                    onChange={setSelectedViews}
                                    isMulti={false}
                                />
                            </div>

                            <div className="row mt-2">
                                <div className="col-md-12 text-end">
                                    <button
                                        type="button"
                                        className="btn"
                                        onClick={() => getTelemetryHistoryData(5, 1)}
                                        style={{
                                            backgroundColor: '#F9B74C',
                                            color: 'black',
                                            fontWeight: 'bold',
                                            width: '150px',
                                            textTransform: 'uppercase',
                                            fontFamily: "'Poppins', sans-serif",
                                            fontSize: '14px',
                                        }}>
                                        Submit
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                {(!selectedViews || selectedViews.value === 'table') && (
                    <div
                        className="col-sm-12 card-alerts my-2 p-2 border-0  text-start no-radius"
                        style={{ width: '100%', position: 'relative' }}>
                        <span
                            onClick={downloadTelemetryHistoryExcel}
                            style={{
                                position: 'absolute',
                                top: '15px',
                                right: '15px',
                                border: '1px solid #ccc',
                                padding: '10px 10px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                            <i className="fa fa-download"></i>
                        </span>

                        <style>{`
                                .telemetry-clickable-rows tbody tr {
                                    cursor: pointer;
                                }
                                .telemetry-clickable-rows tbody tr:hover {
                                    background-color: #eff5ff;
                                }
                            `}</style>
                        <div className="telemetry-clickable-rows" onClick={handleTableRowClick}>
                            <Table
                                columns={TelemetryHistoryColumns}
                                data={data}
                                pageSize={limit}
                                pageCount={pageCount}
                                pageNumber={pageNumber}
                                totalCount={totalCount}
                                sizePerPageList={sizePerPageList}
                                isSortable={true}
                                pagination={true}
                                isSearchable={true}
                                onPageChange={handlePageChange}
                                onPageClick={handlePageClick}
                            />
                        </div>
                    </div>
                )}

                {selectedViews?.value === 'graph' && (
                    <div
                        className="col-sm-12 card-alerts my-2 p-2 border-0 text-start no-radius"
                        style={{ width: '100%', position: 'relative' }}>
                        <div className="row mb-2 gx-2">
                            <MultiSelectDropdown
                                label="Assets Type"
                                options={graphAssetTypeOptions}
                                selectedValues={selectedGraphAssetType}
                                onChange={setSelectedGraphAssetType}
                                isMulti={false}
                            />
                            <MultiSelectDropdown
                                label="Assets Number"
                                options={graphAssetNumberOptions}
                                selectedValues={selectedGraphAssetNumber}
                                onChange={setSelectedGraphAssetNumber}
                                isMulti={false}
                            />
                        </div>

                        <h6
                            className="fw-bold mt-2 mb-1"
                            style={{ fontFamily: "'Poppins', sans-serif", color: '#000' }}>
                            Current
                        </h6>
                        <TelemetryGraphComponent
                            key={`current-${selectedGraphAssetType?.value || 'none'}-${
                                selectedGraphAssetNumber?.value || 'none'
                            }`}
                            categories={currentGraphData?.categories}
                            series={currentGraphData?.series}
                        />

                        <h6
                            className="fw-bold mt-4 mb-1"
                            style={{ fontFamily: "'Poppins', sans-serif", color: '#000' }}>
                            Voltage
                        </h6>
                        <TelemetryGraphComponent
                            key={`voltage-${selectedGraphAssetType?.value || 'none'}-${
                                selectedGraphAssetNumber?.value || 'none'
                            }`}
                            categories={voltageGraphData?.categories}
                            series={voltageGraphData?.series}
                        />
                    </div>
                )}

                {selectedViews?.value === 'circuit' && (
                    <div
                        className="col-sm-12 card-alerts my-2 p-2 border-0  text-start no-radius"
                        style={{ width: '100%', position: 'relative' }}>
                        <div className="row mb-2 gx-2">
                            <MultiSelectDropdown
                                label="Assets Type"
                                options={circuitAssetTypeOptions}
                                selectedValues={selectedCircuitAssetType}
                                onChange={setSelectedCircuitAssetType}
                                isMulti={false}
                            />
                            <MultiSelectDropdown
                                label="Assets Number"
                                options={circuitAssetNumberOptions}
                                selectedValues={selectedCircuitAssetNumber}
                                onChange={setSelectedCircuitAssetNumber}
                                isMulti={false}
                            />
                        </div>

                        {/* <CircuitPage
                            circuit={circuit.filter(
                                (c) =>
                                    c.assetType === selectedCircuitAssetType?.value &&
                                    c.assetNumber === selectedCircuitAssetNumber?.value
                            )}
                        /> */}

                        <CircuitPage
                            circuit={circuit
                                .filter(
                                    (c) =>
                                        c.assetType === selectedCircuitAssetType?.value &&
                                        c.assetNumber === selectedCircuitAssetNumber?.value
                                )
                                .map((c) => ({
                                    ...c,
                                    assetType: mapAssetTypeLabel(c.assetType),
                                    parameters: c.parameters?.map((p) => ({ ...p, name: mapParamLabel(p.name) })),
                                }))}
                        />
                    </div>
                )}
            </div>

            <div className="modal fade" id="statusModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <button
                                type="button"
                                className="btn-close ms-auto"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <div className="modal-body text-center">
                            <img
                                src="/asset-images/checkmark.png"
                                alt="Success"
                                className="img-fluid"
                                style={{ maxWidth: '100px' }}
                            />
                            <p className="mt-2">{statusMessage}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- PARAMETER DETAIL MODAL (History: multi-row, Date & Time + params) ---- */}
            <div className="modal fade" id="parameterDetailModal" tabIndex="-1" aria-hidden="true">
                <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                    <div className="modal-content">
                        <div className="modal-header" style={{ borderBottom: 'none' }}>
                            <h5
                                className="modal-title fw-bold"
                                style={{ fontFamily: "'Poppins', sans-serif", color: '#000' }}>
                                Parameter Details
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"></button>
                        </div>
                        <div className="modal-body">
                            {selectedRowData &&
                                (() => {
                                    const displayRowData = buildGateMergedHistoryRowData(selectedRowData);
                                    return (
                                        <>
                                            <div className="row gx-2 gy-2 mb-3">
                                                {[
                                                    { label: 'Zone', value: selectedRowData.zoneCode },
                                                    { label: 'Division', value: selectedRowData.divisionCode },
                                                    { label: 'Station', value: selectedRowData.stationCode },
                                                    { label: 'Asset Type', value: selectedRowData.assetType },
                                                    { label: 'Asset Number', value: selectedRowData.assetNumber },
                                                ].map((info, idx) => (
                                                    <div className="col-6 col-md-4 col-lg-2" key={idx}>
                                                        <div
                                                            style={{
                                                                border: '1px solid #eee',
                                                                borderRadius: '8px',
                                                                padding: '8px 10px',
                                                                height: '100%',
                                                                backgroundColor: '#fafafa',
                                                            }}>
                                                            <div
                                                                style={{
                                                                    fontSize: '11px',
                                                                    color: '#888',
                                                                    textTransform: 'uppercase',
                                                                    fontWeight: 600,
                                                                    letterSpacing: '0.4px',
                                                                }}>
                                                                {info.label}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    fontSize: '14px',
                                                                    color: '#000',
                                                                    fontWeight: 600,
                                                                    wordBreak: 'break-word',
                                                                    fontFamily: "'Poppins', sans-serif",
                                                                }}>
                                                                {info.value || '-'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div
                                                style={{
                                                    overflowX: 'auto',
                                                    overflowY: 'auto',
                                                    maxHeight: '55vh',
                                                    width: '100%',
                                                    border: '1px solid #dee2e6',
                                                    borderRadius: '6px',
                                                }}>
                                                <table
                                                    className="table table-bordered text-center mb-0"
                                                    style={{ minWidth: 'max-content' }}>
                                                    <thead>
                                                        <tr>
                                                            <th
                                                                style={{
                                                                    backgroundColor: '#eff5ff',
                                                                    color: '#000',
                                                                    fontWeight: 700,
                                                                    whiteSpace: 'nowrap',
                                                                    border: '1px solid #dee2e6',
                                                                    padding: '8px 14px',
                                                                    fontFamily: "'Poppins', sans-serif",
                                                                    position: 'sticky',
                                                                    top: 0,
                                                                    left: 0,
                                                                    zIndex: 2,
                                                                }}>
                                                                Date &amp; Time
                                                            </th>
                                                            {displayRowData.paramKeys?.map((key, idx) => (
                                                                <th
                                                                    key={idx}
                                                                    style={{
                                                                        backgroundColor: '#eff5ff',
                                                                        color: '#000',
                                                                        fontWeight: 700,
                                                                        whiteSpace: 'nowrap',
                                                                        border: '1px solid #dee2e6',
                                                                        padding: '8px 14px',
                                                                        fontFamily: "'Poppins', sans-serif",
                                                                        position: 'sticky',
                                                                        top: 0,
                                                                        zIndex: 1,
                                                                    }}>
                                                                    {key}
                                                                </th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {displayRowData.rows?.map((row, rowIdx) => (
                                                            <tr key={rowIdx}>
                                                                <td
                                                                    style={{
                                                                        whiteSpace: 'nowrap',
                                                                        border: '1px solid #dee2e6',
                                                                        padding: '8px 14px',
                                                                        fontWeight: 600,
                                                                        backgroundColor: '#fff',
                                                                        position: 'sticky',
                                                                        left: 0,
                                                                    }}>
                                                                    {row.dateTime}
                                                                </td>
                                                                {row.values.map((val, colIdx) => (
                                                                    <td
                                                                        key={colIdx}
                                                                        style={{
                                                                            whiteSpace: 'nowrap',
                                                                            border: '1px solid #dee2e6',
                                                                            padding: '8px 14px',
                                                                        }}>
                                                                        {val}
                                                                    </td>
                                                                ))}
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </>
                                    );
                                })()}
                        </div>
                    </div>
                </div>
            </div>
            {/* ---- END MODAL ---- */}
        </>
    );
};

export default TelemetryHistory;
